<!--
  Zimobi fork of components-next/message/MessageError.vue. Deltas:

  1. Known Meta delivery codes are translated to Portuguese; anything else
     falls through to the raw provider text.
  2. A failure with no provider text at all falls back to the generic
     `CONVERSATION.SEND_FAILED` copy, so the tooltip is never empty.
  3. The tooltip honours the \n those translations are written with
     (whitespace-pre-line), breaks on word boundaries instead of mid-word,
     and is w-72 to fit them.
  4. Upstream's retry button is not rendered. On 4.8 the retry endpoint marks
     the message as sent before resending, and SendReplyJob skips any message
     that already has a `source_id`. Every failure Meta reports through a
     status webhook (payment, 24h window, unknown number) therefore ended up
     with a sent tick and was never delivered. Upstream fixed the endpoint in
     v4.17 (#15432); bring the button back once that fix is merged.

  Everything else mirrors upstream. `provider.js` and `constants` are imported
  through the `next` alias so this resolves to the very same modules the parent
  Message.vue uses -- a second instance of provider.js would carry a different
  MessageControl symbol and useMessageContext would stop finding the parent.
-->
<script setup>
import { computed } from 'vue';
import Icon from 'next/icon/Icon.vue';
import { useI18n } from 'vue-i18n';
import { translateExternalError } from 'custom/dashboard/helper/zimobi/messageErrorTranslationsHelper';
import { useMessageContext } from 'next/message/provider.js';
import { ORIENTATION } from 'next/message/constants';

const props = defineProps({
  // Empty whenever the channel reported the failure without a reason.
  error: { type: String, default: '' },
});

const { orientation } = useMessageContext();

const { t } = useI18n();

const errorText = computed(
  () =>
    translateExternalError(props.error) ||
    props.error ||
    t('CONVERSATION.SEND_FAILED')
);
</script>

<template>
  <div class="text-xs text-n-ruby-11 flex items-center gap-1.5">
    <span>{{ t('CHAT_LIST.FAILED_TO_SEND') }}</span>
    <div class="relative group">
      <div
        class="bg-n-alpha-2 rounded-md size-5 grid place-content-center cursor-pointer"
      >
        <Icon
          icon="i-lucide-alert-triangle"
          class="text-n-ruby-11 size-[14px]"
        />
      </div>
      <div
        class="absolute bg-n-alpha-3 px-4 py-3 border rounded-xl border-n-strong text-n-slate-12 bottom-6 w-72 text-xs backdrop-blur-[100px] shadow-[0px_0px_24px_0px_rgba(0,0,0,0.12)] opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all break-words whitespace-pre-line"
        :class="{
          'ltr:left-0 rtl:right-0': orientation === ORIENTATION.LEFT,
          'ltr:right-0 rtl:left-0': orientation === ORIENTATION.RIGHT,
        }"
      >
        {{ errorText }}
      </div>
    </div>
  </div>
</template>
